import React, { useState, useEffect } from 'react'
import TableSkeleton from '../Skeleton/table'

function BaiscTable({header,body,loaded,addRes,canAdd,notClickable,hide,fontSize,empty_table_text}) {
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Extract visible headers (filter out null/undefined)
  const visibleHeaders = header.filter(i => i)

  // For mobile card view, we need to render body rows as cards
  const renderMobileCards = () => {
    if (!body || !body.length) return null

    return (
      <div className="flex flex-col gap-3">
        {React.Children.map(body, (row, rowIndex) => {
          if (!row) return null

          // Get the row's children (td elements)
          const cells = React.Children.toArray(row.props.children)

          // Check if this row is hidden
          if (row.props.hide) return null

          // Check if this row is disabled/not clickable
          const isDisabled = row.props.disabled
          const isNotClickable = row.props.not_clickable || notClickable

          // Filter out hidden cells, options/actions cells, and map to visible headers
          // We separate "option" cells (IsOptions, Actions, MainActions, AdvancedActions) from data cells
          const allVisibleCells = cells.filter((cell) => {
            if (!React.isValidElement(cell)) return false
            return !cell.props.hide
          })

          // Identify action/option cells so we don't render them as data rows
          // (these are rendered separately in the card footer)
          const isActionCell = (cell) => {
            const t = cell?.type
            return (
              cell?.props?.IsOptions ||
              t === BaiscTable.Actions ||
              t === BaiscTable.MainActions ||
              t === BaiscTable.AdvancedActions
            )
          }

          const dataCells = allVisibleCells.filter((cell) => !isActionCell(cell))
          const actionCells = allVisibleCells.filter((cell) => isActionCell(cell))

          return (
            <div
              key={rowIndex}
              onClick={!isDisabled && !isNotClickable ? row.props.onClick : undefined}
              className={`bg-white rounded-lg shadow-sm border border-gray-200 p-4 
                ${isDisabled ? 'opacity-40' : ''} 
                ${isDisabled || isNotClickable ? 'pointer-events-none' : 'cursor-pointer active:opacity-75'}
                ${row.props.className || ''}
              `}
            >
              {dataCells.map((cell, cellIndex) => {
                // Map data cell index to the correct header label
                // (headers align with non-action data cells only)
                const headerLabel = visibleHeaders[cellIndex]
                const cellContent = cell.props.children

                return (
                  <div key={cellIndex} className="flex justify-between items-start gap-4 py-1.5 border-b border-gray-100 last:border-b-0">
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wide min-w-[100px] shrink-0">
                      {headerLabel}
                    </span>
                    <span className="text-sm text-gray-900 text-right flex-1 min-w-0 break-words [overflow-wrap:anywhere] whitespace-normal">
                      {cellContent}
                    </span>
                  </div>
                )
              })}

              {/* Render action/option cells once, in the card footer */}
              {actionCells.length > 0 && (
                <div
                  className="flex justify-end items-center gap-2 mt-3 pt-3 border-t border-gray-100"
                  onClick={(e) => e.stopPropagation()}
                >
                  {actionCells.map((cell, actionIndex) => {
                    // Handle AdvancedActions specially - it returns a fragment with two divs
                    if (cell.type === BaiscTable.AdvancedActions) {
                      const { items, id, w, hide, data } = cell.props
                      const visibleItems = (items || []).filter(i => !i.hide)
                      if (hide || visibleItems.length === 0) return null
                      return (
                        <div key={`action-${actionIndex}`} className="relative">
                          <span
                            className="_table_options cursor-pointer"
                            onClick={(e) => {
                              e.stopPropagation()
                              setTimeout(() => data._showPopUp('table_options', id), 100)
                            }}
                          >
                            
                             <svg  xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#111"><path d="M240-400q-33 0-56.5-23.5T160-480q0-33 23.5-56.5T240-560q33 0 56.5 23.5T320-480q0 33-23.5 56.5T240-400Zm240 0q-33 0-56.5-23.5T400-480q0-33 23.5-56.5T480-560q33 0 56.5 23.5T560-480q0 33-23.5 56.5T480-400Zm240 0q-33 0-56.5-23.5T640-480q0-33 23.5-56.5T720-560q33 0 56.5 23.5T800-480q0 33-23.5 56.5T720-400Z"/></svg>
      
                          </span>
                          {data._openPopUps.table_options == id && (
                            <div className={`z-10 _table_options bg-white absolute right-0 bottom-full mb-1 divide-y divide-gray-100 rounded-lg shadow w-${w ? w : '130'}px`}>
                              <ul className="py-2 text-sm text-gray-700" aria-labelledby="dropdownDefaultButton">
                                {visibleItems.map((i, _i) => (
                                  <li
                                    key={_i}
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      i.onClick && i.onClick(e)
                                    }}
                                    className={`flex ${_i != 0 ? 'border-t' : ''} items-center px-4 hover:bg-gray-100 cursor-pointer`}
                                  >
                                    <span className="mr-2">{i.icon}</span>
                                    <a className="block py-2 text-[14px]">{i.name}</a>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )
                    }
                    // For other action cells (Actions, MainActions), render normally
                    return (
                      <React.Fragment key={`action-${actionIndex}`}>
                        {cell.props.children}
                      </React.Fragment>
                    )
                  })}
                </div>
              )}
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <div className={`${notClickable ? '_not_clickable_table' : ''} ${hide ? 'hidden' : ''} max-h-[1300px] overflow-y-auto`}>
      
      {/* Desktop Table View */}
      {!isMobile && (
        <div className="relative overflow-x-auto shadow-sm sm:rounded-lg">
          <table className={`w-full ${!loaded ? 'hidden' : ''} text-sm text-left rtl:text-right`}>
            <thead className="text-xs text-gray-700 bg-gray-50">
              <tr>
                {visibleHeaders.map((i, _i) => (
                  <th key={_i} scope="col" className={`px-6 py-3 whitespace-nowrap ${_i == 0 ? 'w-[20px]' : ''} ${fontSize ? `text-[${fontSize}px]` : ''}`}>
                    {i}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {body}
            </tbody>
          </table>
        </div>
      )}

      {/* Mobile Card View */}
      {isMobile && loaded && (
        <div className="px-2">
          {renderMobileCards()}
        </div>
      )}

      {/* Loading State */}
      <div className="mt-2">
        {!loaded && <TableSkeleton />}
      </div>

      {/* Empty State */}
      {(loaded && !body.length) && (
        <div className="w-full min-h-[200px] flex items-center justify-center">
          <div className="flex flex-col items-center">
            <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="mb-3">
              <path d="M12 3v18" />
              <rect x="4" y="3" width="16" height="18" rx="2" />
              <path d="M8 8h8" />
              <path d="M8 12h8" />
              <path d="M8 16h8" />
            </svg>
            <p className="text-gray-600">{empty_table_text || 'Nenhum dado encontrado'}</p>
            {canAdd && (
              <button onClick={addRes} type="button" className="text-honolulu_blue-500 cursor-pointer focus:ring-4 border border-honolulu_blue-400 focus:outline-none focus:ring-[#4285F4]/50 font-medium rounded-[0.3rem] text-sm px-4 py-2 mt-3 text-center inline-flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" className="fill-honolulu_blue-400">
                  <path d="M440-440H200v-80h240v-240h80v240h240v80H520v240h-80v-240Z" />
                </svg>
                Adicionar
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

BaiscTable.Tr = ({children,onClick,hide,disabled,not_clickable,fontSize}) => {
  return (
    <tr onClick={onClick} className={`odd:bg-white ${fontSize ? `text-[${fontSize}px]` : ''} ${disabled ? 'opacity-40' : ''} ${disabled || not_clickable ? 'pointer-events-none' : ''} ${hide ? 'hidden' : ''} even:bg-white border-b hover:bg-gray-200 cursor-pointer active:opacity-45`}>
      {children}
    </tr>
  )
}

BaiscTable.Td = ({children,url,hide,onClick,IsOptions}) => {
  return (
    <>
      {onClick && <td onClick={onClick} className={`px-6 whitespace-nowrap ${hide ? 'hidden' : ''} py-4`}>{children}</td>}
      {!onClick && <td onClick={() => {
        //navigate(url)
      }} className={`px-6 whitespace-nowrap ${hide ? 'hidden' : ''} py-4`}>{children}</td>}
    </>
  )
}

BaiscTable.MainActions = ({items,options,hide,data,sigleSelection}) => {
  if (hide) {
    return <></>
  }

  return (
    <div className={`flex items-center _delete ${hide ? 'hidden' : ''}`}>
      <input onClick={() => {
        if (items.length == data._selectedTableItems.length) {
          data.setSelectedTableItems([])
        } else {
          data.setSelectedTableItems(items.map(i => i.id))
        }
      }} checked={data._selectedTableItems.length == items.length} id="checked-checkbox" type="checkbox" value="" className={`w-4 max-md:hidden ${!items.length || sigleSelection ? 'hidden' : ''} mr-1 h-4 cursor-pointer text-blue-600 bg-gray-100 border-gray-300 rounded`} />

      {(items.length != 0 && data._selectedTableItems.length != 0) && (
        <div className="cursor-pointer" onClick={() => {
          data.setItemsToDelete({...data.itemsToDelete,url:options.deleteUrl,items:data._selectedTableItems,deleteFunction:options.deleteFunction})
          data._showPopUp('delete')
        }}>
          <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#5f6368">
            <path d="M280-120q-33 0-56.5-23.5T200-200v-520h-40v-80h200v-40h240v40h200v80h-40v520q0 33-23.5 56.5T680-120H280Zm400-600H280v520h400v-520ZM360-280h80v-360h-80v360Zm160 0h80v-360h-80v360ZM280-720v520-520Z" />
          </svg>
        </div>
      )}
    </div>
  )
}

BaiscTable.AdvancedActions = ({items,id,w,hide,data}) => {
  return (
    <>
      <div></div>
      <div className={`relative z-10 ${hide ? 'hidden' : ''} translate-y-[30px] ${(items || []).filter(i => !i.hide).length == 0 ? 'hidden' : ''} flex items-center`}>
        <span className="_table_options" onClick={() => {
          setTimeout(() => data._showPopUp('table_options', id), 100)
        }}>
          <svg  xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#111">
            <path d="M480-160q-33 0-56.5-23.5T400-240q0-33 23.5-56.5T480-320q33 0 56.5 23.5T560-240q0 33-23.5 56.5T480-160Zm0-240q-33 0-56.5-23.5T400-480q0-33 23.5-56.5T480-560q33 0 56.5 23.5T560-480q0 33-23.5 56.5T480-400Zm0-240q-33 0-56.5-23.5T400-720q0-33 23.5-56.5T480-800q33 0 56.5 23.5T560-720q0 33-23.5 56.5T480-640Z" />
          </svg>
         </span>
        {data._openPopUps.table_options == id && (
          <div id="dropdown" className={`z-10 _table_options bg-white absolute right-[20px] divide-y divide-gray-100 rounded-lg shadow w-[${w ? w : '130'}px]`}>
            <ul className={`py-2 text-sm text-gray-700`} aria-labelledby="dropdownDefaultButton">
              {(items || []).filter(i => !i.hide).map((i, _i) => (
                <li key={_i} onClick={i.onClick} className={`flex ${_i != 0 ? 'border-t' : ''} items-center px-4 hover:bg-gray-100`}>
                  <span className="mr-2">{i.icon}</span>
                  <a className="block py-2 text-[14px]">{i.name}</a>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </>
  )
}

BaiscTable.Actions = ({options,hide,data,sigleSelection}) => {
  return (
    <div className={`flex items-center _delete max-md:justify-end ${hide ? 'hidden' : ''}`}>
      {!sigleSelection && (
        <div onClick={() => data.add_remove_table_item(options.id)} className={`flex items-center mr-1`}>
          <input checked={data._selectedTableItems.includes(options.id)} id="checked-checkbox" type="checkbox" value="" className="w-4 cursor-pointer h-4 text-blue-600 bg-gray-100 border-gray-300 rounded" />
        </div>
      )}
    </div>
  )
}

export default BaiscTable